import React from 'react';
import { RocketLaunchIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';
import ToolCallout from '../../../components/playbooks/ToolCallout';
import { Link } from 'react-router-dom';

const FeedbackCultureStage1 = () => {
  return (
    <div className="space-y-8">
      <PlaybookSection
        title="Stage 1: Baselining Feedback"
        icon={<RocketLaunchIcon className="h-7 w-7 text-primary-light" />}
        heading
      >
        <div className="prose prose-invert max-w-none mb-6">
          <h3 className="text-lg font-semibold text-primary-light">TL;DR</h3>
          <ul className="list-disc pl-5 text-gray-300">
            <li>Set a baseline for why feedback matters and how to do it well.</li>
            <li>Run a lunch & learn to align on principles and techniques.</li>
            <li>Gauge team appetite for sharing feedback preferences.</li>
          </ul>
        </div>
        <ExpandableCallout
          type="info"
          title="What do I need to DO in this stage?"
          defaultOpen={true}
        >
          <div className="space-y-4">
            <p className="text-gray-300">You need to do 2 things:</p>
            <ol className="space-y-2 list-decimal list-inside">
              <li className="text-gray-300">Baseline understanding of why feedback is a good thing</li>
              <li className="text-gray-300">Help folks understand how to deliver and receive great feedback</li>
            </ol>
            <p className="text-gray-300 italic">Here's what we mean...</p>
          </div>
        </ExpandableCallout>

        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4">Build Your Lunch And Learn Deck</h2>
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300 mb-4">
              Whether you are resetting or starting from scratch it's a good idea to start with a baseline on why feedback is a good idea, 
              set up some guiding principles and run an introductory session (lunch and learn) on how to deliver and receive feedback.
            </p>
          </div>
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Example contents for lunch and learn:</h3>
            <PlaybookImage
              src="https://auth.openorg.fyi/storage/v1/object/public/resources/feedback-culture/contents.webp"
              alt="Lunch and Learn Example Contents"
              caption="Example contents for your feedback lunch and learn deck."
            />
          </div>
          <div className="mt-6 space-y-4">
            <h3 className="text-lg font-semibold text-white">There are 2 goals with this session:</h3>
            <ul className="space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                Get everyone on the same page regarding some guiding principles
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                Invite folks to explore some different techniques (async)
              </li>
            </ul>
          </div>
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-white mb-4">When you present:</h3>
            <p className="text-gray-300">
              It's great to have something like sli.do going for this session so you can get some signal on how this is going down 
              and also see what questions/challenges are top of mind for folks on your team(s).
            </p>
          </div>
          <ExpandableCallout
            type="tip"
            title="Template Resource"
          >
            <p className="text-gray-300 mb-4">
              If you don't mind the slightly dramatic gamer design inspo, please feel free to remix this simple Google Slides template:
            </p>
            <PlaybookImage
              src="https://auth.openorg.fyi/storage/v1/object/public/resources/feedback-culture/theart.webp"
              alt="Google Slides Feedback Template Preview"
              caption="Preview of the Google Slides feedback template."
            />
            <ToolCallout
              tool="google"
              label="Google Slides Feedback Template"
              href="https://docs.google.com/presentation/d/1aFPbiIg9kgHkJjmRYBSL4k_R0nKvf_3-TKpGLoKQ_F0/edit?usp=sharing"
              description="Remix this simple Google Slides template for your session."
            />
          </ExpandableCallout>
        </PlaybookCard>

        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4">Invite Discussion</h2>
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300 mb-4">
              So you've hosted your lunch and learn, responded to questions and set some foundational principles...now what?
            </p>
          </div>
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Action:</h3>
            <p className="text-gray-300">
              Drop a follow up to everyone who attended. Get a little poll vote going in slack. Something simple:
            </p>
            <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700/50">
              <p className="text-gray-300 italic">
                🙋‍♀️ hands up if you think'd it be a good next step if we shared with each other how we like to get feedback?
              </p>
            </div>
            <p className="text-gray-300 mt-4">
              This is your test point: is this something people really want?
            </p>
            <ul className="space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span>
                  If emoji love rains down upon you. Proceed to <Link to="/playbooks/feedback-culture/stage-2" className="text-primary hover:text-primary-light underline">Stage 2</Link> ;)
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                If it's crickets/luke warm. Follow up with a few folks 121 to figure out what the barrier is.
              </li>
            </ul>
          </div>
        </PlaybookCard>
      </PlaybookSection>
    </div>
  );
};

export default FeedbackCultureStage1; 